import React from "react";
import { X } from "react-feather";
import { Autocomplete, TextField, IconButton, Button, Checkbox, Paper, ClickAwayListener } from "@mui/material";
import NumberFormat from "react-number-format";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";

// Filter fields shared by the prospect grid (prospectlist.jsx) and list (prospect-list-view) views.

export const PROSPECT_OPTIONS = [
  { value: "applied", label: "Applied" },
  { value: "not_applied", label: "Not Applied" },
  { value: "screened", label: "Screened" },
  { value: "approved", label: "Approved" },
  { value: "declined", label: "Declined" },
  { value: "airbnb", label: "Airbnb Guest" },
  { value: "archived", label: "Archived" },
];

const PROSPECT_ALL_OPTION = { value: "all", label: "All Prospects" };

export const CREDIT_OPTIONS = [
  { value: "all", label: "All Credit Scores" },
  { value: "0,649", label: "Below 650" },
  { value: "650,0", label: "650 & Above" },
];

export const SALARY_OPTIONS = [
  { value: "all", label: "All Salary" },
  { value: "0,20000", label: "< $20,000" },
  { value: "20000,40000", label: "$20,000 - $40,000" },
  { value: "40000,60000", label: "$40,000 - $60,000" },
  { value: "60000,80000", label: "$60,000 - $80,000" },
  { value: "80000,0", label: "> $80,000" },
];

const CUSTOM_OPTION = { value: "custom", label: "Custom" };

const CreditScoreField = React.forwardRef((props, ref) => {
  return (<NumberFormat customInput={TextField} prefix="" displayType="input" format={"###"} {...props} />);
});

const MoneyField = React.forwardRef((props, ref) => {
  return (<NumberFormat customInput={TextField} prefix="" displayType="input" type="text" thousandSeparator={true} allowNegative={false} decimalScale={0} {...props} />);
});

const RANGE_FIELDS = {
  credit: { Field: CreditScoreField, minLabel: "Min Score", maxLabel: "Max Score" },
  salary: { Field: MoneyField, minLabel: "Min Salary", maxLabel: "Max Salary" },
};

const rangeSchema = yup.object().shape({
  min: yup.string().required("This field is Required"),
  max: yup.string().required("This field is Required"),
});

// Multi-select prospect status filter with an "All Prospects" toggle. value/onChange use the status keys, e.g. ["applied", "screened"].
export const ProspectStatusAutocomplete = ({ value, onChange }) => {
  const selectedProspects = value || [];
  const isAllSelected = PROSPECT_OPTIONS.every(i => selectedProspects.indexOf(i.value) > -1);
  const isSomeSelected = PROSPECT_OPTIONS.some(i => selectedProspects.indexOf(i.value) > -1);

  return (<Autocomplete
    fullWidth
    multiple
    disableClearable
    disableCloseOnSelect
    options={[PROSPECT_ALL_OPTION].concat(PROSPECT_OPTIONS)}
    value={selectedProspects.map(v => PROSPECT_OPTIONS.find(i => i.value === v)).filter(Boolean)}
    getOptionLabel={(option) => option?.label || ""}
    isOptionEqualToValue={(option, selected) => option?.value === selected?.value}
    onChange={(e, newValue, reason, details) => {
      if (details?.option?.value === "all") {
        onChange(isAllSelected ? [] : PROSPECT_OPTIONS.map(i => i.value));
        return;
      }

      onChange(newValue.filter(i => i.value !== "all").map(i => i.value));
    }}
    renderTags={(tags) => {
      if (tags.length === PROSPECT_OPTIONS.length)
        return <span>All selected</span>;
      else
        return <span>{tags.length + ' selected'}</span>;
    }}
    renderOption={(props, option, { selected }) => (<li {...props} key={option.value}>
      {option.value === "all" ? (<Checkbox indeterminate={!isAllSelected && isSomeSelected} checked={isAllSelected} />) : (<Checkbox checked={selected} />)}
      {option.label}
    </li>)}
    renderInput={(params) => (<TextField {...params} label="Prospects" placeholder={selectedProspects.length === 0 ? "None Selected" : ""} />)}
  />);
};

// Popup paper that renders an extra footer (the custom range form) below the option list.
const PaperWithFooter = ({ footer, children, ...props }) => {
  return (<Paper {...props}>
    {children}
    {footer}
  </Paper>);
};

// Credit / salary range filter: preset options, saved custom ranges (deletable) and a "Custom" option that opens a min/max form.
// rangeType is "credit" or "salary"; onApply receives { min, max } from the custom form.
export const RangeFilterAutocomplete = ({ label, rangeType, value, options, open, setOpen, onSelect, onClear, onDelete, addCustom, setAddCustom, onApply, onCancel, styles }) => {
  const { Field, minLabel, maxLabel } = RANGE_FIELDS[rangeType];
  const { handleSubmit, control } = useForm({ resolver: yupResolver(rangeSchema) });

  const allOptions = options.concat(CUSTOM_OPTION);
  const selectedOption = value ? (allOptions.find(item => item?.value === value) || { value, label: value }) : null;

  const renderRangeField = (name, fieldLabel) => (<div className={`${styles.CustomFiletrField} FormGroup`}>
    <Controller
      name={name}
      control={control}
      render={(field) => (<Field
        {...field}
        label={fieldLabel}
        variant="outlined"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        onChange={(e) => {
          e.stopPropagation();
          field.onChange(e);
        }}
      />)}
    />
  </div>);

  const customForm = (<form onSubmit={handleSubmit(onApply)}>
    <div className={`${styles.CustomFilterSecRow}`}>
      {renderRangeField("min", minLabel)}
      {renderRangeField("max", maxLabel)}
      <div className={`${styles.CustomFiletrBUsec}`}>
        <Button onClick={(e) => onCancel()} className={`${styles.CancelPopupBU}`}>Cancel</Button>
        <Button className={`${styles.SubmitPopupBU}`} type="submit">Apply</Button>
      </div>
    </div>
  </form>);

  return (<Autocomplete
    fullWidth
    options={allOptions}
    value={selectedOption}
    open={open}
    onOpen={(e) => setOpen(true)}
    onClose={(e, reason) => {
      // Keep the popup open while the user types in the custom range form
      if (addCustom && reason === "blur")
        return;
      setOpen(false);
    }}
    getOptionLabel={(option) => option?.label || ""}
    isOptionEqualToValue={(option, selected) => option?.value === selected?.value}
    onChange={(e, newValue) => {
      if (!newValue) {
        onClear();
        return;
      }
      if (newValue.value === "custom")
        setAddCustom(true);
      onSelect(newValue.value);
    }}
    renderOption={(props, option) => (<li {...props} key={option.value} className={option.value === "custom" ? `${props.className} ${styles.customcredit}` : props.className}>
      {option.label}
      {option?.is_custom === 1 && <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(option.value);
        }}
      >
        <X fontSize="small" />
      </IconButton>}
    </li>)}
    PaperComponent={PaperWithFooter}
    slotProps={{
      paper: {
        footer: addCustom ? (<ClickAwayListener onClickAway={(e) => setOpen(false)}>
          <div className={`${styles.CustomFilterSec}`}>{customForm}</div>
        </ClickAwayListener>) : null,
      },
    }}
    renderInput={(params) => (<TextField {...params} label={label} />)}
  />);
};
